import { useEffect, useMemo, useState } from 'react';
import { Card, DataTable, SkeletonBodyText, Text } from '@shopify/polaris';
import { formatToCurrency, getRequest } from '../../../../shared/components/functions';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { formatPercent, toNumber } from './analyticsHelpers';

// Revenue or costs split per business unit per year. BU tagging on invoices started in 2025.
export default function BusinessUnitCard({
  title = 'Revenue per business unit',
  subtitle = 'Outgoing invoice totals (incl. VAT) split per business unit. Business units are tagged on invoices since 2025, so earlier years are not shown.',
  endpoint = '/api/metrics/revenue-by-business-unit'
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      const result = await getRequest(endpoint);
      setData(Array.isArray(result) ? result : []);
      setLoading(false);
    }
    fetchData();
  }, [endpoint]);

  const { units, tableRows } = useMemo(() => {
    const totalsByUnit = {};
    data.forEach((row) => {
      totalsByUnit[row.business_unit] = (totalsByUnit[row.business_unit] || 0) + toNumber(row.total_incl);
    });
    const unitNames = Object.keys(totalsByUnit).sort((a, b) => totalsByUnit[b] - totalsByUnit[a]);

    const years = [...new Set(data.map((row) => Number(row.year)))].sort((a, b) => b - a);
    const rows = years.map((year) => {
      const yearRows = data.filter((row) => Number(row.year) === year);
      const yearTotal = yearRows.reduce((sum, row) => sum + toNumber(row.total_incl), 0);
      const isCurrentYear = year === new Date().getFullYear();
      return [
        <b>{isCurrentYear ? `${year} (YTD)` : year}</b>,
        ...unitNames.map((unit) => {
          const match = yearRows.find((row) => row.business_unit === unit);
          const total = toNumber(match?.total_incl);
          if (!total) return '—';
          return `${formatToCurrency(total)} (${yearTotal ? formatPercent(total / yearTotal) : '—'})`;
        }),
        formatToCurrency(yearTotal)
      ];
    });
    return { units: unitNames, tableRows: rows };
  }, [data]);

  return (
    <Card>
      <CardHeading title={title} />
      <Text tone="subdued" as="p">
        {subtitle}
      </Text>
      <div style={{ marginTop: '12px' }}>
        {loading ? (
          <SkeletonBodyText lines={4} />
        ) : (
          <DataTable
            verticalAlign="middle"
            increasedTableDensity
            columnContentTypes={['text', ...units.map(() => 'numeric'), 'numeric']}
            headings={[<b>Year</b>, ...units.map((unit) => <b>{unit}</b>), <b>Total</b>]}
            rows={tableRows}
          />
        )}
      </div>
    </Card>
  );
}
